/**
 * Hook useCalendarMonthSummary
 *
 * Consome a RPC unificada `get_calendar_unified_events` em MODO MÊS.
 *
 * A tela de mês precisa entregar ao FullCalendar todos os eventos do range já
 * na abertura. Isso mantém o popover nativo "+N mais" funcionando no primeiro
 * clique, sem carregamento sob demanda e sem clique programático.
 *
 * Para não voltar ao limite de 1000 linhas do PostgREST, a busca é feita por
 * cursor server-side (`p_limit` + `p_cursor_start_at` + `p_cursor_id`). Cada
 * página também traz os agregados `local_day`, `day_total` e `total_count` que
 * alimentam o contador do cabeçalho e o texto correto dos links "+N mais".
 *
 * RELEASE:
 *   2026-07-25 v3.2.0 - perDay/totalCount agora vêm da RPC dedicada
 *                        `get_calendar_month_day_counts` (fonte autoritativa),
 *                        corrigindo o rótulo "+N mais" que subestimava o total
 *                        real do dia. Em caso de falha, faz fallback silencioso
 *                        para os agregados vindos das páginas.
 *   2026-07-25 v3.1.0 - Correção definitiva do popover "+N mais": remove o
 *                        top-K mensal e volta a carregar o mês completo via
 *                        cursor server-side, preservando a paginação para não
 *                        limitar em 1000 registros e permitindo o popover
 *                        nativo abrir completo no primeiro clique.
 *   2026-07-25 v3.0.0 - Reintroduz top-K + RPC leve de contadores. Remove
 *                        o carregamento do mês inteiro por cursor (v2.1.0)
 *                        que gerava payloads de milhares de linhas em
 *                        tenants com alta densidade de eventos.
 *   2026-07-25 v2.1.0 - Cursor server-side (mês completo em páginas).
 *   2026-07-25 v2.0.0 - Passa a chamar `get_calendar_unified_events`.
 *   2026-07-24 v1.0.0 - Versão inicial (fix do cap de 1000 no Mês).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type {
  CalendarEventSourceType,
  UnifiedCalendarEvent,
} from '@/hooks/useCalendarUnifiedEvents';

const logger = createLogger('useCalendarMonthSummary');

/** Contrato do payload devolvido ao consumidor (Calendar.tsx). */
export interface CalendarMonthSummary {
  /** Total EXATO de eventos no mês (independe do top-K). */
  totalCount: number;
  /** Mapa `YYYY-MM-DD` → total real de eventos naquele dia. */
  perDay: Record<string, number>;
  /** Eventos completos do range mensal, carregados por páginas. */
  events: UnifiedCalendarEvent[];
}

interface UseMonthSummaryParams {
  /** Início da janela (ISO) — normalmente 1º dia do grid do mês. */
  start: string;
  /** Fim da janela (ISO) — normalmente último dia do grid + 1. */
  end: string;
  /** Calendários selecionados; null = todos visíveis. */
  calendarIds?: string[] | null;
  /** Filtro por tipo de fonte; null = todas. */
  sourceTypes?: CalendarEventSourceType[] | null;
  /** Mostrar eventos concluídos/cancelados. */
  includeFinished?: boolean;
  /** Habilita/desabilita a query. */
  enabled?: boolean;
}

/** Formato das linhas vindas da RPC mensal paginada. */
type RawRow = UnifiedCalendarEvent & {
  id: string;
  start_at: string;
  local_day: string | null;
  day_total: number | null;
  total_count: number | null;
};

/** Tamanho de cada página do mês; casa com o limite padrão do PostgREST. */
const MONTH_PAGE_SIZE = 1000;

/** Trava defensiva para evitar loop infinito caso o cursor venha inconsistente. */
const MAX_MONTH_PAGES = 50;

/**
 * Busca o mês completo por páginas, mantendo contadores exatos por dia.
 */
export function useCalendarMonthSummary(params: UseMonthSummaryParams) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  const {
    start,
    end,
    calendarIds,
    sourceTypes,
    includeFinished = true,
    enabled = true,
  } = params;

  return useQuery<CalendarMonthSummary>({
    queryKey: [
      'calendar-month-summary',
      tenantId,
      start,
      end,
      calendarIds ?? 'all',
      sourceTypes ?? 'all',
      includeFinished,
    ],
    enabled: !!tenantId && !!start && !!end && enabled,
    staleTime: 30_000,
    queryFn: async () => {
      logger.info('Buscando mês completo do calendário via cursor', {
        start,
        end,
        includeFinished,
      });

      const rows: RawRow[] = [];
      let cursorStartAt: string | null = null;
      let cursorId: string | null = null;

      // ─────────────────────────────────────────────────────────────────────
      // (A) Dispara em paralelo a RPC dedicada de contadores por dia.
      // Fonte AUTORITATIVA para perDay/totalCount — o rótulo "+N mais" e o
      // contador do cabeçalho passam a bater com o popover nativo do FC.
      // ─────────────────────────────────────────────────────────────────────
      const dayCountsPromise = supabase.rpc(
        'get_calendar_month_day_counts',
        {
          p_start: start,
          p_end: end,
          p_calendar_ids: calendarIds ?? null,
          p_source_types: sourceTypes ?? null,
          p_timezone: 'America/Sao_Paulo',
          p_include_finished: includeFinished,
          p_tenant_hint: tenantId ?? null,
        } as never,
      );

      // ─────────────────────────────────────────────────────────────────────
      // (B) Busca paginada das linhas completas do mês (para o FullCalendar).
      // ─────────────────────────────────────────────────────────────────────
      for (let page = 0; page < MAX_MONTH_PAGES; page += 1) {
        const { data, error } = await supabase.rpc(
          'get_calendar_unified_events',
          {
            p_start: start,
            p_end: end,
            p_calendar_ids: calendarIds ?? null,
            p_source_types: sourceTypes ?? null,
            p_timezone: 'America/Sao_Paulo',
            p_tenant_hint: tenantId ?? null,
            p_include_finished: includeFinished,
            // Não enviar p_per_day_limit: o FullCalendar precisa receber o
            // mês completo para o popover nativo "+N mais" abrir completo.
            p_limit: MONTH_PAGE_SIZE,
            p_cursor_start_at: cursorStartAt,
            p_cursor_id: cursorId,
          } as never,
        );

        if (error) {
          logger.error('Erro ao buscar página mensal do calendário', error);
          throw error;
        }

        const pageRows = (data ?? []) as unknown as RawRow[];
        rows.push(...pageRows);

        if (pageRows.length < MONTH_PAGE_SIZE) {
          break;
        }

        const lastRow = pageRows[pageRows.length - 1];
        if (!lastRow?.start_at || !lastRow.id) {
          logger.warn('Cursor mensal interrompido por linha final inválida', {
            page,
            received: pageRows.length,
          });
          break;
        }

        cursorStartAt = lastRow.start_at;
        cursorId = lastRow.id;
      }

      if (rows.length >= MONTH_PAGE_SIZE * MAX_MONTH_PAGES) {
        logger.warn('Limite defensivo de páginas mensais atingido', {
          loaded: rows.length,
          maxPages: MAX_MONTH_PAGES,
        });
      }

      // ─────────────────────────────────────────────────────────────────────
      // (C) Consolida contadores: prioriza a RPC dedicada; em falha, cai
      // para os agregados vindos das páginas (comportamento anterior).
      // ─────────────────────────────────────────────────────────────────────
      let perDay: Record<string, number> = {};
      let totalCount = 0;

      try {
        const { data: countsData, error: countsError } = await dayCountsPromise;
        if (countsError) throw countsError;

        const countRows = (countsData ?? []) as unknown as Array<{
          local_day: string | null;
          day_total: number | null;
          total_count: number | string | null;
        }>;

        for (const r of countRows) {
          if (r.local_day) {
            perDay[r.local_day] = Number(r.day_total ?? 0);
          }
        }
        // total_count é repetido em todas as linhas — pega a primeira válida.
        const firstWithTotal = countRows.find((r) => r.total_count != null);
        totalCount = firstWithTotal ? Number(firstWithTotal.total_count) : 0;
      } catch (err) {
        logger.warn(
          'Falha em get_calendar_month_day_counts — usando agregados das páginas',
          err,
        );
        // Fallback: reaproveita day_total / total_count das linhas paginadas.
        perDay = {};
        for (const row of rows) {
          if (row.local_day && perDay[row.local_day] === undefined) {
            perDay[row.local_day] = Number(row.day_total ?? 0);
          }
        }
        totalCount = rows.length > 0 ? Number(rows[0].total_count ?? 0) : 0;
      }

      return {
        totalCount,
        perDay,
        events: rows as UnifiedCalendarEvent[],
      };
    },
  });
}
